import {
  type ReactElement,
  type PropsWithChildren,
  type ReactNode,
  type MouseEventHandler,
  forwardRef,
  type ButtonHTMLAttributes,
} from 'react'
import { ButtonCopy } from 'app/components/buttons'
import { Link } from '@tanstack/react-router'
import {
  Button,
  type ButtonProps,
  createPolymorphicComponent,
  Group,
  type GroupProps,
  Stack,
  Tooltip,
} from '@mantine/core'
import { Size } from '@front/model'
import { LabelDetails, type LabelDetailsProps } from './LabelDetails'

type ClickableListItem = {
  tooltip?: string
  onClick: MouseEventHandler<HTMLButtonElement>
  buttonProps?: Omit<ButtonProps, 'onClick'>
}

export type TListItemProps = PropsWithChildren<
  {
    rightSection?: ReactNode
    leftSection?: ReactNode
    containerProps?: Omit<GroupProps, 'onClick'>
    canCopy?: boolean
    copyValue?: string
  } & LabelDetailsProps &
    (ClickableListItem | { href: string } | {})
>

export const TListItem = (listItemProps: TListItemProps): ReactElement => {
  if ('onClick' in listItemProps) {
    const { onClick, buttonProps, tooltip, ...itemProps } = listItemProps
    const buttonElement = (
      <ListItemButton {...buttonProps} onClick={onClick}>
        <TListItem {...itemProps} />
      </ListItemButton>
    )
    if (tooltip) {
      return <Tooltip label={tooltip}>{buttonElement}</Tooltip>
    }
    return buttonElement
  }
  if ('href' in listItemProps) {
    const { href, ...itemProps } = listItemProps
    return (
      <ListItemButton component={Link} to={href}>
        <TListItem {...itemProps} />
      </ListItemButton>
    )
  }

  return <ListItemContent {...listItemProps} />
}

const ListItemContent = ({
  leftSection,
  rightSection,
  containerProps,
  canCopy,
  copyValue,
  children,
  ...props
}: TListItemProps): ReactElement => {
  const copyText = getCopyText(copyValue, props)
  return (
    <Group
      gap={Size.space['2xl']}
      justify='space-between'
      wrap='nowrap'
      p={Size.space['2xl']}
      w='100%'
      miw={0}
      {...containerProps}
    >
      {leftSection}
      <Stack flex={1} miw={0} gap={Size.space.md} className='text-start'>
        <LabelDetails {...props} />
        {children}
      </Stack>
      <Group gap={Size.space['2xl']}>
        {canCopy && copyText && <ButtonCopy value={copyText} />}
        {rightSection}
      </Group>
    </Group>
  )
}

const getCopyText = (copyValue?: string, props?: LabelDetailsProps): string | undefined => {
  if (copyValue) return copyValue
  const value = props?.detailBold ? props?.details : props?.label
  if (typeof value !== 'string') return
  return value
}

type ListItemButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps> & ButtonProps
const _ListItemButton = forwardRef<HTMLButtonElement, ListItemButtonProps>((props, ref) => {
  return (
    <Button
      color='neutral'
      justify='stretch'
      variant='subtle'
      w='100%'
      {...props}
      styles={{
        ...props.styles,
        label: {
          width: '100%',
          flex: 1,
          textWrap: 'wrap',
        },
      }}
      style={{
        height: 'fit-content',
        padding: 0,
        ...props.style,
      }}
      ref={ref}
    />
  )
})

const ListItemButton = createPolymorphicComponent<'button', ListItemButtonProps>(_ListItemButton)
